import React, { useState, useEffect, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Menu,
  MenuItem,
  ListItemText,
  ListItemIcon,
} from "@material-ui/core";
import TemplateCard from "./../TemplateCard/TemplateCard";
import { useSelector } from "react-redux";
import CompletedFormDialog from "../CompletedFormDialog/CompletedFormDialog";
import { getRequestedFormsByTemplateId } from "../../../../services/templateV2Service";
import moment from "moment";
import TablePagination from "@mui/material/TablePagination";
import {
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
} from "@material-ui/icons";
import iconDocTpl from "../../../../assets/templateV2/folder_yellow.svg";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BaseDataTable } from "../../../../components/common/BaseDataTable";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import SkeletonLoader from '../SkeletonLoader/SkeletonLoader';

const useStyles = makeStyles(() => ({
  container: {
    width: "100%",
    height: "100%",
    borderRadius: 0,
    backgroundColor: "#fff",
    display: "inline-block",
    padding: "10px",
    position: "relative",
    overflowY: "auto",
    borderRadius: "8px",
  },
  gridContainer: {
    display: "grid",
    gridGap: "14px",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gridAutoRows: "120px",
    padding: "8px 0",
  },
  skeleton: {
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    animation: "$skeleton-loading 1.5s infinite ease-in-out",
  },
  "@keyframes skeleton-loading": {
    "0%": { backgroundColor: "#f0f0f0" },
    "50%": { backgroundColor: "#e0e0e0" },
    "100%": { backgroundColor: "#f0f0f0" },
  },
}));

const optionsMenu = [
  { label: "Details", value: "Details", icon: <VisibilityIcon /> },
];

export default function CompletedView({
  setLoading,
  viewMode = "grid",
  onSort,
  sortBy,
  sortOrder,
}) {
  const classes = useStyles();
  const notify = (msj, event) => toast[event](msj);

  const isPdfFile = (t) => {
    const candidate =
      t?.pdfUrl || t?.pathUrl || t?.fileName || t?.templateName || "";
    return String(candidate).toLowerCase().includes(".pdf");
  };


  // Redux search slice (same source as Templates tab)
  const { responseSearchForm, loading } = useSelector((state) => ({
    responseSearchForm: state.Search.responseSearchForm,
    loading: state.Search.loadingSearchForm,
  }));

  // Always provide a NEW array reference so React re-renders after sort
  const listDocuments = useMemo(() => [...(responseSearchForm?.data || [])], [responseSearchForm?.data]);
  const listDocumentsTotal =
    responseSearchForm?.header?.totalRecords ?? listDocuments.length;

  const [requestedDocumentsByTemplate, setRequestedDocumentsByTemplate] =
    useState([]);
  const [openCompletedFormDialog, setOpenCompletedFormDialog] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // Local pagination (0-based for MUI)
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    // log helps confirm new order + props
    // eslint-disable-next-line no-console
    console.log("CompletedView templates updated", {
      first: listDocuments?.[0],
      sortBy,
      sortOrder,
      count: listDocuments.length,
    });
  }, [listDocuments, sortBy, sortOrder]);

  const handleMenuClick = (event, template) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedTemplate(template);
  };
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedTemplate(null);
  };

  const formatDate = (dateString) =>
    dateString ? moment(dateString).format("MM/DD/YYYY") : "N/A";

  const handleChangePage = (_e, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  // For server-side paging we just render what's provided
  const getCurrentPageItems = () => listDocuments;


  const handleClickMenuItem = (menu, template) => {
    switch (menu) {
      case "Details":
        handleViewDetails(template);
        break;
      default:
        break;
    }
  };

  const handleViewDetails = async (template) => {
    const body = { isCompleted: true, formTemplateId: template.id };
    try {
      setLoading(true);
      const response = await getRequestedFormsByTemplateId(body);
      if (response.data.data.length > 0) {
        setRequestedDocumentsByTemplate(response.data.data);
        setOpenCompletedFormDialog(true);
      } else {
        // Handle case where no documents are found
        notify("No completed documents found for this template.", "info");
      }
    } catch (err) {
      // noop/log as needed
    } finally {
      setLoading(false);
    }
  };

  const onCloseCompleteModal = () => setOpenCompletedFormDialog(false);

  return (
    <div className={classes.container}>
      {!loading && listDocumentsTotal > 50 && (
        <TablePagination
          component="div"
          count={listDocumentsTotal}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
          showFirstButton
          showLastButton
        />
      )}

      {loading ? (
        <SkeletonLoader viewMode={viewMode} itemCount={rowsPerPage} />
      ) : (
        <>
          {viewMode === "grid" ? (
            <div className={classes.gridContainer}>
              {getCurrentPageItems().map((template, idx) => (
                <TemplateCard
                  key={`${template.id}-${idx}`}
                  docIcon={iconDocTpl}
                  optionsMenu={optionsMenu}
                  template={template}
                  onClickMenu={handleClickMenuItem}
                />
              ))}
            </div>
          ) : (
            <BaseDataTable
              data={getCurrentPageItems().map((template) => ({
                ...template,
                id: template.id || template.formTemplateId,
                fileSize: template.fileSizeFormatted || "N/A",
              }))}
              columns={[
                {
                  field: "templateName",
                  headerName: "Name",
                  flex: 3,
                  renderCell: (params) => (
                    <div className={classes.templateNameContainer}>
                      {isPdfFile(params.row) && (
                        <PictureAsPdfIcon style={{ color: "#E53935", marginRight: 4 }} />
                      )}
                      <div className={classes.templateName}>{params.row.templateName}</div>
                    </div>
                  ),
                },
                {
                  field: "creatorRole",
                  headerName: "Team",
                  flex: 1,
                },
                {
                  field: "fileSize",
                  headerName: "Size",
                  flex: 1,
                },
                {
                  field: "createdAt",
                  headerName: "Date Added",
                  flex: 1.5,
                  valueFormatter: (params) => formatDate(params.value),
                },
                {
                  field: "actions",
                  headerName: "",
                  flex: 0.1,
                  renderCell: (params) => (
                    <MoreVertIcon
                      onClick={(e) => handleMenuClick(e, params.row)}
                      style={{ cursor: "pointer" }}
                    />
                  ),
                },
              ]}
              loading={loading}
              onMenuOptionClick={handleClickMenuItem}
              option={({ action, id, row }) => {
                handleClickMenuItem(action, row);
              }}
            />
          )}
        </>
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          onClick={() => handleClickMenuItem("Details", selectedTemplate)}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" style={{ color: "#0076FF" }} />
          </ListItemIcon>
          <ListItemText primary="Details" />
        </MenuItem>
      </Menu>

      <CompletedFormDialog
        open={openCompletedFormDialog}
        onClose={onCloseCompleteModal}
        requestedDocumentsByTemplate={requestedDocumentsByTemplate}
      />
    </div>
  );
}
